import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import AdminShell from "@/components/admin/AdminShell";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, Trash2, Pencil, RefreshCw } from "lucide-react";

const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/blog/posts");
      setPosts(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const togglePublish = async (p) => {
    try {
      await api.patch(`/admin/blog/posts/${p.id}`, null, {
        params: { published: !p.published },
      });
      setPosts((ps) =>
        ps.map((x) => (x.id === p.id ? { ...x, published: !p.published } : x))
      );
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const removePost = async (p) => {
    if (!window.confirm(`Beitrag "${p.slug}" löschen?`)) return;
    try {
      await api.delete(`/admin/blog/posts/${p.id}`);
      setPosts((ps) => ps.filter((x) => x.id !== p.id));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <AdminShell
      title="Magazin"
      subtitle={`Alle Magazin-Beiträge · ${posts.length}`}
      actions={
        <>
          <button
            onClick={load}
            data-testid="admin-blog-refresh"
            className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm hover:bg-neutral-50"
          >
            <RefreshCw className="w-4 h-4" />
            Aktualisieren
          </button>
          <Link
            to="/admin/blog/new"
            data-testid="admin-new-post-btn"
            className="inline-flex items-center gap-2 rounded-full bg-neutral-950 text-white px-4 py-2 text-sm hover:bg-black"
          >
            <Plus className="w-4 h-4" />
            Neuer Beitrag
          </Link>
        </>
      }
    >
      <div className="rounded-3xl bg-white border border-neutral-200 overflow-hidden">
        {loading ? (
          <div className="p-12 grid place-items-center text-neutral-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : posts.length === 0 ? (
          <div className="p-12 text-center text-neutral-500" data-testid="admin-blog-empty">
            Noch keine Beiträge.
          </div>
        ) : (
          <Table data-testid="admin-blog-table">
            <TableHeader>
              <TableRow>
                <TableHead>Slug</TableHead>
                <TableHead>Title (DE)</TableHead>
                <TableHead>Tags</TableHead>
                <TableHead>Erstellt</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {posts.map((p) => (
                <TableRow key={p.id} data-testid={`admin-blog-row-${p.slug}`}>
                  <TableCell>
                    <a
                      href={`/blog/${p.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sky-700 hover:underline"
                    >
                      /{p.slug}
                    </a>
                  </TableCell>
                  <TableCell className="font-medium max-w-md">
                    <div className="line-clamp-2">{p.title?.de}</div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(p.tags || []).slice(0, 3).map((t) => (
                        <Badge key={t} variant="secondary" className="rounded-full">
                          {t}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-neutral-500 whitespace-nowrap text-xs">
                    {new Date(p.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <button
                      onClick={() => togglePublish(p)}
                      data-testid={`admin-blog-publish-${p.slug}`}
                    >
                      <Badge
                        className={`rounded-full ${
                          p.published
                            ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                            : "bg-neutral-200 text-neutral-700"
                        }`}
                      >
                        {p.published ? "Live" : "Draft"}
                      </Badge>
                    </button>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Link
                        to={`/admin/blog/edit/${p.id}`}
                        data-testid={`admin-blog-edit-${p.slug}`}
                        className="inline-flex items-center gap-1 text-sky-700 hover:text-sky-800 text-sm"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                        Bearbeiten
                      </Link>
                      <button
                        onClick={() => removePost(p)}
                        data-testid={`admin-blog-delete-${p.slug}`}
                        className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 text-sm"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Löschen
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </AdminShell>
  );
};

export default AdminBlog;
